import type { ReactNode } from 'react'
import { useNls } from '../../nls/nlsProvider'
import type { NlsKey } from '../../nls/strings_nls'

export type StatusMarkTone = 'OK' | 'Low' | 'Critical' | 'Expiring' | 'Waiting'

export interface StatusMarkProps {
  /** Tone mapped to Mosaic status tokens */
  tone: StatusMarkTone
  /** Visible label text. If omitted, square mark is rendered with sr-only accessible label. */
  label?: ReactNode
  /** Accessible label. Defaults to caller label or localized tone name. */
  ariaLabel?: string
  className?: string
  testId?: string
}

/**
 * Status tokens mapped to Mosaic theme tokens:
 * - OK: var(--success)
 * - Low: var(--warning)
 * - Critical: var(--danger)
 * - Expiring: color-mix(in srgb, var(--warning) 70%, var(--danger))
 * - Waiting: var(--muted)
 */
const TONE_BG_CLASSES: Record<StatusMarkTone, string> = {
  OK: 'bg-[var(--success)]',
  Low: 'bg-[var(--warning)]',
  Critical: 'bg-[var(--danger)]',
  Expiring: 'bg-[color-mix(in_srgb,var(--warning)_70%,var(--danger))]',
  Waiting: 'bg-[var(--muted)]',
}

const TONE_NLS_KEYS: Record<StatusMarkTone, NlsKey> = {
  OK: 'NLSStatusMarkToneOk',
  Low: 'NLSStatusMarkToneLow',
  Critical: 'NLSStatusMarkToneCritical',
  Expiring: 'NLSStatusMarkToneExpiring',
  Waiting: 'NLSStatusMarkToneWaiting',
}

/**
 * StatusMark — 8px square color swatch + status text for ledger tables and inventory lists.
 * Tones OK / Low / Critical / Expiring / Waiting mapped to Mosaic status tokens.
 * Static mark with swatch aria-hidden and accessible text from visible label or sr-only tone.
 */
export function StatusMark({
  tone,
  label,
  ariaLabel,
  className,
  testId,
}: StatusMarkProps) {
  const { t } = useNls()
  const fallbackLabel = t(TONE_NLS_KEYS[tone] ?? 'NLSStatusMarkToneWaiting')
  const swatchClass = TONE_BG_CLASSES[tone] ?? TONE_BG_CLASSES.Waiting

  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-xs select-none ${className ?? ''}`}
    >
      <span
        aria-hidden="true"
        data-tone={tone}
        className={`w-2 h-2 rounded-[2px] flex-none ${swatchClass}`}
      />
      {label !== undefined ? (
        <span className="text-[var(--text-strong)] font-medium leading-none">
          {label}
        </span>
      ) : (
        <span className="sr-only">
          {ariaLabel ?? fallbackLabel}
        </span>
      )}
    </span>
  )
}
